import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { errorMessage, request } from '../api';

interface ClientRow {
  id: string;
  name: string;
  slug: string;
  timezone: string;
  status: string;
  profiles: number;
  bidders: number;
  bidsLast30Days: number;
  generationsLast30Days: number;
}

export function Clients(): JSX.Element {
  const clients = useQuery({
    queryKey: ['admin', 'clients'],
    queryFn: () => request<ClientRow[]>('GET', '/admin/clients'),
  });

  return (
    <>
      <header className="page-head">
        <h1>Clients</h1>
        <p className="muted small" style={{ margin: 0 }}>
          Every tenant on this instance, and the door new ones come in through.
        </p>
      </header>

      <div className="stack">
        <CreateClient />

        <section className="card">
          <strong>Clients</strong>
          {clients.isLoading && <p className="muted small">Loading…</p>}
          {clients.data && (
            <table style={{ marginTop: 10 }}>
              <thead>
                <tr>
                  <th>Client</th>
                  <th>Timezone</th>
                  <th>Profiles</th>
                  <th>Bidders</th>
                  <th>Bids 30d</th>
                  <th>Generations 30d</th>
                </tr>
              </thead>
              <tbody>
                {clients.data.map((client) => (
                  <tr key={client.id}>
                    <td>
                      {client.name}
                      <div className="muted small">{client.slug}</div>
                    </td>
                    <td className="muted">{client.timezone}</td>
                    <td className="muted">{client.profiles}</td>
                    <td className="muted">{client.bidders}</td>
                    <td className="muted">{client.bidsLast30Days}</td>
                    {/*
                      Higher than bids by design — the gap is abandoned
                      generations, which still consumed a provider call and are
                      what the caps are counted against.
                    */}
                    <td className="muted">{client.generationsLast30Days}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
    </>
  );
}

/**
 * Onboard a client: the tenant and its first CLIENT login are created together
 * (`POST /admin/clients`), because a tenant nobody can sign in to is not a
 * usable state. Slug and username are derived from name and email as a
 * convenience but stay editable — deriving stops the moment they're touched.
 */
function CreateClient(): JSX.Element {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [timezone, setTimezone] = useState(
    () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  );
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerUsername, setOwnerUsername] = useState('');
  const [usernameTouched, setUsernameTouched] = useState(false);
  const [ownerPassword, setOwnerPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [createdEmail, setCreatedEmail] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      request<{ clientId: string; ownerUserId: string }>('POST', '/admin/clients', {
        name,
        slug,
        timezone,
        ownerEmail,
        ownerUsername,
        ownerPassword,
      }),
    onSuccess: () => {
      setCreatedEmail(ownerEmail);
      setError(null);
      setName('');
      setSlug('');
      setSlugTouched(false);
      setOwnerEmail('');
      setOwnerUsername('');
      setUsernameTouched(false);
      setOwnerPassword('');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] });
    },
    onError: (caught) => setError(errorMessage(caught)),
  });

  const tooShort = ownerPassword.length > 0 && ownerPassword.length < 12;
  const ready =
    name.trim() && slug.trim() && timezone.trim() && ownerEmail.trim() && ownerUsername.trim() &&
    ownerPassword.length >= 12;

  return (
    <section className="card">
      <strong>Create a client</strong>
      <p className="muted small" style={{ marginTop: 4 }}>
        Creates the tenant and its first portal login in one step. Send them the email and
        password you set here — there is no public signup.
      </p>

      <div className="grid2" style={{ marginTop: 10 }}>
        <div className="field">
          <label>Company / client name</label>
          <input
            value={name}
            placeholder="Acme Corp"
            onChange={(e) => {
              setName(e.target.value);
              if (!slugTouched) setSlug(deriveSlug(e.target.value));
            }}
          />
        </div>
        <div className="field">
          <label>Slug — used to disambiguate logins; hard to change later</label>
          <input
            value={slug}
            placeholder="acme-corp"
            onChange={(e) => {
              setSlug(e.target.value.toLowerCase());
              setSlugTouched(true);
            }}
          />
        </div>
        <div className="field">
          <label>Reporting timezone (IANA) — daily bid grouping uses this</label>
          <input
            value={timezone}
            placeholder="America/New_York"
            onChange={(e) => setTimezone(e.target.value)}
          />
        </div>
        <div className="field">
          <label>Their login email</label>
          <input
            type="email"
            value={ownerEmail}
            placeholder="owner@client.com"
            onChange={(e) => {
              setOwnerEmail(e.target.value);
              if (!usernameTouched) setOwnerUsername(deriveUsername(e.target.value));
            }}
          />
        </div>
        <div className="field">
          <label>Their username</label>
          <input
            value={ownerUsername}
            placeholder="owner"
            onChange={(e) => {
              setOwnerUsername(e.target.value);
              setUsernameTouched(true);
            }}
          />
        </div>
        <div className="field">
          <label>Their password (at least 12 characters)</label>
          {/* Visible, not masked: the admin has to send it to the client anyway. */}
          <div style={{ display: 'flex', gap: 8 }}>
            <input
              value={ownerPassword}
              placeholder="Set or generate"
              onChange={(e) => setOwnerPassword(e.target.value)}
              autoComplete="off"
            />
            <button type="button" className="sm" onClick={() => setOwnerPassword(generatePassword())}>
              Generate
            </button>
          </div>
          {tooShort && <span className="warn small">Use at least 12 characters.</span>}
        </div>
      </div>

      {error && <p className="error small">{error}</p>}
      {createdEmail && !error && (
        <p className="ok small">
          Created. {createdEmail} can now sign in to the client portal with the password you set.
        </p>
      )}

      <button
        className="primary"
        onClick={() => create.mutate()}
        disabled={!ready || create.isPending}
      >
        {create.isPending ? 'Creating…' : 'Create client'}
      </button>
    </section>
  );
}

function deriveSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

function deriveUsername(email: string): string {
  return (email.split('@')[0] ?? '').replace(/[^a-zA-Z0-9._-]/g, '').slice(0, 50);
}

function generatePassword(): string {
  // No ambiguous characters (0/O, 1/l/I) — this gets read aloud or retyped.
  const alphabet = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789';
  const bytes = new Uint8Array(20);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => alphabet[byte % alphabet.length]).join('');
}
